<script setup lang="ts">
import {reactive, ref} from "vue";
import {Dialog, Icon, Menu} from "@/Components/Library";
import type {MenuItem} from "primevue/menuitem";
import {toggleConversation} from "@/Services/conversations";
import {Tag} from "@/Types";
import {useToast} from "primevue/usetoast";
import {useMainStore} from "@/Stores/store";

type Conversation = {
    id: number;
    title: string;
    tags: Array<Tag>;
    pivot: {
        user_id: number;
        conversation_id: number;
        is_archived: number;
        is_pinned: number;
        last_read_at: string;
        last_read_message_id: number;
    };
    participants: Array<ConversationParticipant>;
};

type ConversationParticipant = {
    id: string;
    pivot: {
        user_id: number;
        conversation_id: number;
        is_hidden: number;
    };
};

type ConversationLocal = Omit<Conversation, "tags"> & {
    is_ai_chat?: boolean;
    group_id?: string;
    unread_message_count?: number;
    pivot: {
        user_id: number;
        conversation_id: number;
        is_archived: number;
        is_pinned: number;
        last_read_at: string;
        last_read_message_id: number;
    };
    participants: Array<Omit<ConversationParticipant, "profile">>;
};

const toast = useToast();
const $store = useMainStore();

const props = defineProps<{
    conversation: ConversationLocal;
}>();
const conversation = ref(props.conversation);
const emit = defineEmits(["archived", "pinned"]);

const state = reactive({
    show: {
        conversationMenu: false,
    },
});

const menuContainer = ref();
const showMenu = () => {
    initMenuItems();
    state.show.conversationMenu = true;
};

const archive = async () => {
    try {
        console.log("Archive");
        const hasUnreadMessages = conversation.value?.unread_message_count > 0;
        const newValue = !conversation.value?.pivot?.is_archived;
        await toggleConversation(conversation.value.id, {
            property: "is_archived",
            value: newValue,
        });

        if (hasUnreadMessages) {
            if (!conversation.value.pivot.is_archived) {
                $store.decrementUnreadConversations();
            } else {
                $store.incrementUnreadConversations();
            }
        }

        conversation.value.pivot.is_archived = newValue;
        state.show.conversationMenu = false;
        toast.add({
            severity: "success",
            summary: "Success",
            detail: newValue ? "Chat archived successfully" : "Conversation restored successfully",
            life: 3000,
        });
    } catch (error) {
        console.log(error);
    }
};

const pin = async () => {
    try {
        console.log("Pin");
        const newValue = !conversation.value?.pivot?.is_pinned;
        await toggleConversation(conversation.value.id, {
            property: "is_pinned",
            value: newValue,
        });
        conversation.value.pivot.is_pinned = newValue;
        state.show.conversationMenu = false;
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Chat pinned successfully",
            life: 3000,
        });
    } catch (error) {
        console.log(error);
    }
};

const menuItems = ref<Array<MenuItem>>([]);
const initMenuItems = () =>
    (menuItems.value = [
        {
            label: conversation.value?.pivot?.is_pinned ? "Unpin" : "Pin",
            icon: conversation.value?.pivot?.is_pinned ? "solar:pin-broken" : "solar:pin-bold",
            command: pin,
        },
        {
            label: conversation.value?.pivot?.is_archived ? "Restore" : "Archive",
            icon: conversation.value?.pivot?.is_archived ? "fluent:eye-24-filled" : "fluent:eye-off-24-filled",
            command: archive,
        },
    ]);
</script>
<template>
    <div
        class="mb-3 flex items-center rounded-lg border border-stone-300 bg-white shadow transition hover:shadow-lg dark:border-stone-800 dark:bg-stone-900"
    >
        <div class="flex items-center justify-start" v-auto-animate>
            <template v-if="conversation.is_ai_chat">
                <span class="py-5 pl-4 pr-3">
                    <Icon icon="ri:robot-2-fill" height="20" class="text-askr-green-500" />
                </span>
            </template>
            <template v-else>
                <button @click="showMenu" class="py-5 pl-4 pr-3">
                    <Icon icon="mdi:dots-vertical" height="16" />
                </button>
            </template>
            <span class="pr-3" v-if="conversation.pivot.is_pinned">
                <Icon icon="solar:pin-bold" width="20" class="text-2xl text-askr-green-500" />
            </span>
            <span class="pr-3" v-if="conversation.group_id">
                <Icon icon="prime:users" width="20" class="text-2xl text-stone-400" />
            </span>
        </div>
        <RouterLink
            :to="
                conversation.is_ai_chat
                    ? {name: 'conversations.ai.help'}
                    : {name: 'conversations.show', params: {conversation: conversation.id}}
            "
            class="flex grow items-center py-3 pr-2"
        >
            <h6 class="font-display mr-auto text-lg font-semibold">{{ conversation.title }}</h6>
            <span
                v-if="conversation.unread_message_count"
                class="ml-4 mr-2 flex min-w-7 items-center justify-center rounded-full bg-red-600 px-2 py-0.5 font-semibold text-white"
            >
                {{ conversation.unread_message_count ?? 0 }}
            </span>
            <Icon icon="octicon:chevron-right-12" width="28" />
        </RouterLink>
        <Dialog
            v-if="!conversation.is_ai_chat"
            v-model:visible="state.show.conversationMenu"
            modal
            dismissable-mask
            class="max-w-md overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white">
                    <Menu ref="menuContainer" :model="menuItems" />
                </div>
            </template>
        </Dialog>
    </div>
</template>
<style scoped></style>
